import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// Credit Card Calculator — single card, two payoff modes
export default function CreditCardCalc() {
  const [balance, setBalance] = useState(8000);
  const [rate, setRate] = useState(18);
  const [mode, setMode] = useState('amount');
  const [amount, setAmount] = useState(200);
  const [years, setYears] = useState(2);
  const [months, setMonths] = useState(0);

  const c = useMemo(() => {
    const r = rate/100/12;
    let payment;
    let nExact;
    if (mode === 'amount') {
      if (amount <= balance * r) return { months: Infinity, totalInt: 0, totalPaid: 0, series: [] };
      nExact = -Math.log(1 - balance*r/amount) / Math.log(1 + r);
      payment = amount;
    } else {
      nExact = years*12 + months;
      payment = r === 0 ? balance/nExact : (balance*r*Math.pow(1+r,nExact))/(Math.pow(1+r,nExact)-1);
    }
    // Simulate payoff month-by-month for exact totals (final month may be partial)
    let bal = balance;
    const series = [{ month: 0, balance: bal }];
    let totalPaid = 0, totalInt = 0, m = 0;
    while (bal > 0.005 && m < 1200) {
      m++;
      const interest = bal * r;
      totalInt += interest;
      let pay = payment;
      if (bal + interest < payment) {  // final partial payment — pay only remaining balance + its interest
        pay = bal + interest;
      }
      bal = Math.max(0, bal + interest - pay);
      totalPaid += pay;
      if (m % 3 === 0 || bal <= 0) series.push({ month: m, balance: bal });
    }
    return { months: m, monthly: payment, totalPaid, totalInt, series };
  }, [balance, rate, mode, amount, years, months]);

  return (
    <CalcShell look="cnet" title="Credit Card Calculator" subtitle="Find the time to pay off a balance, or the amount needed to pay it off within a target timeframe."
      about={<><p>This calculator determines how long it will take to pay off a credit card at a given rate, or reverses to find what payment is needed to clear the debt within a set timeframe.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Credit card balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold"><input type="radio" checked={mode==='amount'} onChange={()=>setMode('amount')}/> Pay a certain amount</label>
            {mode==='amount' && <div className="ml-6"><label className="text-xs text-slate-600">pay</label><div className="flex gap-2 items-center mt-1"><span className="text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/><span className="text-slate-500 text-sm">per month</span></div>
              <p className="text-xs text-slate-500 mt-1">or use: <button className="text-blue-700 underline" onClick={()=>setAmount(balance*rate/100/12 + balance*0.01)}>Interest + 1% of Balance</button>, <button className="text-blue-700 underline" onClick={()=>setAmount(balance*0.02)}>2%</button>, <button className="text-blue-700 underline" onClick={()=>setAmount(balance*0.03)}>3%</button>, <button className="text-blue-700 underline" onClick={()=>setAmount(balance*0.04)}>4%</button>, <button className="text-blue-700 underline" onClick={()=>setAmount(balance*0.05)}>5%</button></p>
            </div>}
            <label className="flex items-center gap-2 text-sm font-semibold"><input type="radio" checked={mode==='time'} onChange={()=>setMode('time')}/> Pay off within a certain timeframe</label>
            {mode==='time' && <div className="ml-6 grid grid-cols-2 gap-2"><div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div><div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div></div>}
          </div>
        </div>
        <div>
          <div className="result-panel mb-4">
            <div className="result-title">{mode==='amount'?'Payoff time':'Monthly payment needed'}</div>
            <div className="result-value" data-testid="cc-result">{mode==='amount' ? (isFinite(c.months) ? `${Math.floor(c.months/12)} years ${c.months%12} months` : 'Never — payment too low') : fmt(c.monthly)}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Total paid</td><td className="text-right">{fmt(c.totalPaid)}</td></tr>
            <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.totalInt)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal',value:balance,color:'#2563eb'},{label:'Interest',value:c.totalInt,color:'#dc2626'}]} size={170}/></div>
        </div>
      </div>

      {/* Balance-over-time line chart */}
      {c.series && c.series.length > 1 && (
        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Balance Over Time</h3>
          {(() => {
            const w = 640, h = 240, padL = 60, padR = 20, padT = 15, padB = 32;
            const max = balance;
            const nMax = c.series[c.series.length-1].month;
            const xs = m => padL + (m / (nMax || 1)) * (w - padL - padR);
            const ys = v => h - padB - (v / max) * (h - padT - padB);
            const line = c.series.map(p => `${xs(p.month)},${ys(p.balance)}`).join(' ');
            return (
              <div className="overflow-auto">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[500px]">
                  {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                    <g key={i}>
                      <line x1={padL} y1={ys(t*max)} x2={w - padR} y2={ys(t*max)} stroke="#e2e8f0" strokeDasharray="3 3"/>
                      <text x={padL - 6} y={ys(t*max) + 4} textAnchor="end" fontSize="10" fill="#64748b">${Math.round(t*max/1000)}K</text>
                    </g>
                  ))}
                  <polyline points={line} fill="none" stroke="#2563eb" strokeWidth="2.5"/>
                  <polygon points={`${padL},${h-padB} ${line} ${xs(nMax)},${h-padB}`} fill="#2563eb" opacity="0.15"/>
                  <text x={padL} y={h - padB + 14} fontSize="10" fill="#64748b">Month 0</text>
                  <text x={xs(nMax) - 30} y={h - padB + 14} fontSize="10" fill="#64748b">Month {nMax}</text>
                </svg>
                <div className="flex justify-center mt-2 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#2563eb'}}/>Remaining balance</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </CalcShell>
  );
}
